import React from 'react';
import { softDeleteTimelineEntry } from '../../../services/timeline/timelineEntryDeletionService';
import TimelineDeleteConfirmation from '../TimelineDeleteConfirmation';

const useTimelineDeleteFlow = ({ childId, deleteEntry = softDeleteTimelineEntry }) => {
  const [actionLoadingId, setActionLoadingId] = React.useState(null);
  const [deletedEntryIds, setDeletedEntryIds] = React.useState({});
  const [pendingEntry, setPendingEntry] = React.useState(null);
  const [deleteError, setDeleteError] = React.useState('');
  const deletingRef = React.useRef(false);

  React.useEffect(() => {
    setPendingEntry(null);
    setDeleteError('');
  }, [childId]);

  const handleDeleteEntry = React.useCallback((entry) => {
    if (deletingRef.current) return;
    setDeleteError('');
    setPendingEntry(entry);
  }, []);

  const cancelDelete = React.useCallback(() => {
    if (deletingRef.current) return;
    setPendingEntry(null);
    setDeleteError('');
  }, []);

  const confirmDelete = React.useCallback(async () => {
    if (!pendingEntry || deletingRef.current) return;
    const entry = pendingEntry;
    deletingRef.current = true;
    setActionLoadingId(entry.id);
    setDeleteError('');
    try {
      const result = await deleteEntry(entry);
      setDeletedEntryIds((prev) => ({ ...prev, ...Object.fromEntries((result?.entryIds || [entry.id]).map(id => [id, true])) }));
      setPendingEntry(null);
      window.dispatchEvent(new CustomEvent('captureez:timeline-refresh', {
        detail: { childId: entry.childId || childId || null },
      }));
    } catch (error) {
      setDeleteError(error?.code === 'permission-denied'
        ? 'You do not have permission to delete this entry. Ask the Care Owner for help.'
        : ['unsupported-entry', 'unauthenticated', 'aborted', 'failed-precondition', 'resource-exhausted', 'not-found', 'invalid-argument'].includes(error?.code)
          ? error.message
          : 'We could not delete this entry. It is still saved. Check your connection and try again.');
    } finally {
      deletingRef.current = false;
      setActionLoadingId(null);
    }
  }, [childId, deleteEntry, pendingEntry]);

  return {
    actionLoadingId,
    deletedEntryIds,
    handleDeleteEntry,
    deleteConfirmation: <TimelineDeleteConfirmation open={Boolean(pendingEntry)}
      title={pendingEntry?.kind === 'story' ? 'Delete Story?' : 'Delete entry?'}
      confirmLabel={pendingEntry?.kind === 'story' ? 'Delete Story' : 'Delete entry'}
      message={pendingEntry?.kind === 'story' ? 'All Timeline entries from this capture will be removed. Linked approved facts will be flagged for review.' : 'This entry will be removed from Timeline. Linked approved facts will be flagged for review.'}
      busy={Boolean(actionLoadingId)} error={deleteError} onCancel={cancelDelete} onConfirm={confirmDelete} />,
  };
};

export default useTimelineDeleteFlow;
